import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import InfoIcon from '@mui/icons-material/Info'
import ErrorIcon from '@mui/icons-material/Error'
import type { OrderTypeAmountAlert } from '@/store/ticketSlice'
import { useFormatMessage } from '@/translations/useFormatMessage'
import { useIntl } from 'react-intl'
import { formatPrice } from '@/lib/formatPrice'
import { AlertShell, AlertContent, AlertText } from './OrderTypeMinAlert.styled'

interface Props {
	alert: OrderTypeAmountAlert
	// Bumped each time checkout is retried while the warning is already showing.
	// Passed to AlertContent as a CSS animation key — React remounts the element
	// on each key change, restarting the keyframe animation without any setState.
	nudgeKey: number
}

export function OrderTypeMinAlert({ alert, nudgeKey }: Props) {
	const { t } = useFormatMessage()
	const intl = useIntl()

	const formattedDiff = formatPrice(alert.differenceCents, intl.locale, 'USD')
	const formattedAmount = formatPrice(alert.amountCents, intl.locale, 'USD')

	let message: string
	let icon: React.ReactNode
	let ariaLabel: string

	if (alert.variant === 'above-max') {
		message = t('component:orderTypeMinAlert:aboveMax', {
			amount: formattedDiff,
			orderTypeName: alert.orderTypeName,
		})
		icon = (
			<ErrorIcon
				sx={{ fontSize: 24, flexShrink: 0, color: 'error.main' }}
				aria-hidden="true"
			/>
		)
		ariaLabel = message
	} else if (alert.variant === 'min-reached') {
		message = t('component:orderTypeMinAlert:minReached', {
			amount: formattedAmount,
			orderTypeName: alert.orderTypeName,
		})
		icon = (
			<CheckCircleIcon
				sx={{ fontSize: 24, flexShrink: 0, color: 'success.main' }}
				aria-hidden="true"
			/>
		)
		ariaLabel = message
	} else {
		message = t('component:orderTypeMinAlert:belowMin', {
			amount: formattedDiff,
			orderTypeName: alert.orderTypeName,
		})
		icon = (
			<InfoIcon
				sx={{ fontSize: 24, flexShrink: 0, color: 'warning.main' }}
				aria-hidden="true"
			/>
		)
		ariaLabel = message
	}

	// nudgeKey is passed as a React key to AlertContent — React remounts the element
	// on each increment, restarting the CSS keyframe animation without needing setState.
	const contentKey = alert.variant === 'below-min' && nudgeKey > 0 ? nudgeKey : undefined

	return (
		<AlertShell isVisible role="status" aria-live="polite" aria-label={ariaLabel}>
			<AlertContent
				key={contentKey}
				alertVariant={alert.variant}
				isNudging={alert.variant === 'below-min' && nudgeKey > 0}
			>
				{icon}
				<AlertText>{message}</AlertText>
			</AlertContent>
		</AlertShell>
	)
}
